export type CoverageDeductibleOptionLike = {
  value: string;
  maxSi?: number | null;
  minSi?: number | null;
  /** Faixas de desconto por SI — fallback de faixa quando minSi/maxSi faltam. */
  discount?: { siMin?: number | null; siMax?: number | null }[] | null;
};

export function parseCoverageCurrency(value: string): number {
  const numeric = parseInt(String(value || '').replace(/\D/g, ''), 10);
  if (Number.isNaN(numeric)) return 0;
  return numeric / 100;
}

function formatCurrencyBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Coberturas de Lucros Cessantes / Despesas Fixas: só podem ser preenchidas
 * depois que o valor de Lucros Cessantes/Despesas Fixas do local for informado.
 */
export function isBusinessInterruptionCoverage(name: string): boolean {
  return /lucros\s+cessantes|despesas\s+fixas/i.test(name);
}

export type NormalFlowCoverageCheckInput = {
  name: string;
  required?: boolean;
  lmiValue: number;
  riskValueLimit: number;
  minLimit: number;
  maxLimit: number;
  /**
   * Teto percentual do limite (`limit.percent` do catálogo) sobre o LMI da
   * cobertura básica. Só se aplica quando ambos estão presentes/positivos.
   */
  percentOfBasicLimit?: number | null;
  /** LMI informado para a cobertura básica do local. */
  basicCoverageLmi?: number;
  isFireCoverage: boolean;
  hasDeductibleOptions: boolean;
  hasDeductibleSelected: boolean;
  isSelectedDeductibleValidForLmi: boolean;
};

/**
 * Teto em reais derivado do percentual do limite sobre o LMI da cobertura
 * básica (arredondado ao centavo para evitar ruído de ponto flutuante).
 * `undefined` quando não há percentual ou a cobertura básica não foi
 * preenchida — a validação percentual não se aplica nesses casos.
 */
export function getPercentCapValue(
  percentOfBasicLimit: number | null | undefined,
  basicCoverageLmi: number | undefined
): number | undefined {
  if (percentOfBasicLimit == null || percentOfBasicLimit <= 0)
    return undefined;
  if (basicCoverageLmi == null || basicCoverageLmi <= 0) return undefined;
  return Math.round(percentOfBasicLimit * basicCoverageLmi * 100) / 100;
}

function formatPercentBR(ratio: number): string {
  return `${(ratio * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

/**
 * Mensagem de bloqueio usada na jornada normal de cotação.
 */
export function getNormalFlowCoverageBlockingMessage(
  input: NormalFlowCoverageCheckInput
): string | undefined {
  const {
    name,
    required,
    lmiValue,
    riskValueLimit,
    minLimit,
    maxLimit,
    percentOfBasicLimit,
    basicCoverageLmi,
    isFireCoverage,
    hasDeductibleOptions,
    hasDeductibleSelected,
    isSelectedDeductibleValidForLmi,
  } = input;

  if (required && lmiValue <= 0) {
    return `A cobertura "${name}" é obrigatória. Informe o LMI.`;
  }

  if (lmiValue <= 0) return undefined;

  if (riskValueLimit > 0 && lmiValue > riskValueLimit) {
    return `Valor para a cobertura ${name} excedeu o máximo do valor em risco informado de ${formatCurrencyBRL(riskValueLimit)}.`;
  }

  if (isFireCoverage) {
    if (lmiValue > maxLimit) {
      return `Valor máximo para "${name}" é ${formatCurrencyBRL(maxLimit)}.`;
    }
  } else {
    if (lmiValue < minLimit) {
      return `Valor mínimo para "${name}" é ${formatCurrencyBRL(minLimit)}.`;
    }
    if (lmiValue > maxLimit) {
      return `Valor máximo para "${name}" é ${formatCurrencyBRL(maxLimit)}.`;
    }
  }

  // Teto percentual sobre o LMI da cobertura básica: valida ANTES das regras
  // de franquia — o valor precisa estar dentro do limite para que a seleção
  // de franquia sequer faça sentido.
  const percentCap = getPercentCapValue(percentOfBasicLimit, basicCoverageLmi);
  if (
    percentOfBasicLimit != null &&
    percentCap !== undefined &&
    lmiValue > percentCap
  ) {
    return `Valor máximo para "${name}" é ${formatPercentBR(percentOfBasicLimit)} do LMI da cobertura básica (${formatCurrencyBRL(percentCap)}).`;
  }

  if (hasDeductibleOptions && lmiValue > 0 && !hasDeductibleSelected) {
    return 'Selecione a franquia para esta cobertura antes de avançar.';
  }

  if (
    hasDeductibleOptions &&
    hasDeductibleSelected &&
    !isSelectedDeductibleValidForLmi
  ) {
    return 'A franquia selecionada não é compatível com o LMI informado.';
  }

  return undefined;
}

/**
 * Aviso informativo na jornada de análise — não bloqueia o preenchimento.
 */
export function getNormalFlowCoverageAnalysisWarning(
  input: NormalFlowCoverageCheckInput
): string | undefined {
  const blockingMessage = getNormalFlowCoverageBlockingMessage(input);
  if (!blockingMessage) return undefined;

  return `Esta condição não seria aceita no fluxo normal de cotação: ${blockingMessage}`;
}

export function isDeductibleOptionValidForLmi(
  option: CoverageDeductibleOptionLike,
  lmiValue: number
): boolean {
  if (option.maxSi != null && lmiValue > option.maxSi) return false;
  if (option.minSi != null && lmiValue < option.minSi) return false;
  // Sem faixa explícita de SI, a faixa efetiva vem das faixas de desconto
  // (ex.: franquia "5 dias" de Lucros Cessantes com desconto só até 5M não
  // vale para LMI de 6M). Sem desconto também, a franquia vale para qualquer SI.
  if (option.maxSi == null && option.minSi == null && lmiValue > 0) {
    const bands = option.discount ?? [];
    if (bands.length > 0) {
      return bands.some(
        band =>
          (band.siMin == null || lmiValue >= band.siMin) &&
          (band.siMax == null || lmiValue <= band.siMax)
      );
    }
  }
  return true;
}

export type CoverageCatalogItemForValidation = {
  id: string;
  name: string;
  required?: boolean;
  limit?: {
    constraints?: { min?: number | null; max?: number | null } | null;
    percent?: number | null;
  } | null;
};

/**
 * LMI informado para a cobertura básica (a obrigatória) do local — base do
 * teto percentual (`limit.percent`) das demais coberturas.
 */
export function getBasicCoverageLmi(
  coverages: CoverageCatalogItemForValidation[],
  coverageValues: Record<string, { lmi: string }>
): number {
  const basicCoverage = coverages.find(coverage => coverage.required);
  if (!basicCoverage) return 0;
  return parseCoverageCurrency(coverageValues[basicCoverage.id]?.lmi ?? '');
}

export function collectNormalFlowCoverageBlockingMessages(
  coverages: CoverageCatalogItemForValidation[],
  coverageValues: Record<string, { lmi: string; franquia: string }>,
  riskValue: string,
  deductibleOptionsByCoverageId: Record<string, CoverageDeductibleOptionLike[]> = {}
): Record<string, string> {
  const errors: Record<string, string> = {};
  const riskValueLimit = parseCoverageCurrency(riskValue);
  const basicCoverageLmi = getBasicCoverageLmi(coverages, coverageValues);

  for (const coverage of coverages) {
    const stored = coverageValues[coverage.id];
    const lmiValue = parseCoverageCurrency(stored?.lmi ?? '');
    const deductibleOptions = deductibleOptionsByCoverageId[coverage.id] ?? [];
    const selectedDeductible = deductibleOptions.find(
      option => option.value === stored?.franquia
    );

    const message = getNormalFlowCoverageBlockingMessage({
      name: coverage.name,
      required: coverage.required,
      lmiValue,
      riskValueLimit,
      minLimit: coverage.limit?.constraints?.min ?? 0,
      maxLimit:
        coverage.limit?.constraints?.max ?? Number.MAX_SAFE_INTEGER,
      percentOfBasicLimit: coverage.required
        ? undefined
        : coverage.limit?.percent,
      basicCoverageLmi,
      isFireCoverage: /inc[eê]ndio/i.test(coverage.name),
      hasDeductibleOptions: deductibleOptions.length > 0,
      hasDeductibleSelected: Boolean(stored?.franquia?.trim()),
      isSelectedDeductibleValidForLmi: selectedDeductible
        ? isDeductibleOptionValidForLmi(selectedDeductible, lmiValue)
        : true,
    });

    if (message) {
      errors[coverage.id] = message;
    }
  }

  return errors;
}
